import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import LCD from '../Features/LCD';

export default class LCDTextWidgetNode extends BaseNode {
    constructor() {
        super();
        this.name = 'LCD Text Widget';
        this.id = 'node_lcd_text_widget';
        this.linkedFeature = 'display_lib';
        this.icon = 'CgScreen';

        this.inputs = [
            {
                name: 'Draw Text',
                portId: 'lcd_print_text',
                params: [
                    {
                        name: 'text',
                        dataType: TibboDataType.STRING,
                        default: '',
                    },
                ],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'WIDGET',
                name: 'Widget',
                settingType: SettingType.OPTION,
                refId: 'lcd_text_widget',
                required: true,
                default: '',
            },
            {
                id: 'VISIBLE',
                name: 'Visibliity',
                settingType: SettingType.OPTION,
                default: '1',
                options: [
                    {
                        value: '1',
                        name: 'Visible',
                    },
                    {
                        value: '0',
                        name: 'Hidden',
                    },
                ],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        return super.getSettingOptions(settingId);
    }

    getName(): string {
        const project: ProjectConfiguration = this.project;
        const links = project.getNodeLinks(this, 'lcd_print_text');
        if (links.length === 0) return this.name;
        const widget = this.getSettingValue('WIDGET');
        if (!widget) return '';
        const name = project.getFeature(widget)?.getSettingValue('NAME');
        const text = project.getNodeParameterValue(this, 'text');
        return `Set {{$${name}}} text to ${text}`;
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            return this.getName();
        }
        return super.getSettingValue(settingId);
    }


    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project: ProjectConfiguration = this.project;
        const widget = project.getFeature(this.getSettingValue('WIDGET'));
        const visible = this.getSettingValue('VISIBLE');
        if (!widget) {
            return blocks;
        }
        const screen = project.getParent(widget);
        if (!screen) {
            return blocks;
        }
        const LCDFeature = project.getParent(screen);
        if (!LCDFeature) {
            return blocks;
        }
        const LCDWidgets = LCDFeature.getSettingValue('WIDGETS');
        let textWidgetIndex = 0;
        let widgetIndex = 0;

        let LCD_TEXT_WIDGETS_COUNT = 0;
        let LCD_IMG_WIDGETS_COUNT = 0;
        for (let i = 0; i < project.features.length; i++) {
            const feature = project.features[i];
            for (let j = 0; j < feature.settings.length; j++) {
                const setting = feature.settings[j];
                if (setting.refId === 'display_screen'
                    && setting.settingType === SettingType.ARRAY) {
                    const SCREENS = feature.getSettingValue('SCREENS');
                    for (let k = 0; k < SCREENS.length; k++) {
                        const widgets = SCREENS[k].getSettingValue('WIDGETS');
                        for (let l = 0; l < widgets.length; l++) {
                            if (widgets[l].id === 'lcd_text_widget') {
                                if (widget?.getID() === widgets[l].getID()) {
                                    textWidgetIndex = LCD_TEXT_WIDGETS_COUNT;
                                    break;
                                }
                                LCD_TEXT_WIDGETS_COUNT++;
                            } else if (widgets[l].id === 'lcd_image_widget') {
                                LCD_IMG_WIDGETS_COUNT++;
                            }
                            widgetIndex++;
                        }
                    }
                }
            }
        }

        if (!widget) return blocks;

        const links = project.getNodeLinks(this, 'lcd_print_text');
        const doneLinks = project.getNodeLinks(this, 'done');

        const doneBlock = doneLinks.length > 0 ? `${project.getCodeLocationStart()}${doneLinks[0].getSourceId()}${project.getCodeLocationEnd()}` : '';

        for (let i = 0; i < links.length; i++) {
            const link = links[i];
            switch (project.runtime) {
                case 'tios':
                    blocks.push({
                        location: link.getSourceId(),
                        blockType: BlockType.CODE,
                        id: link.getID(),
                        template: `
        lcd_text_widgets(${textWidgetIndex}).text = ${project.getNodeParameter(link, 'text')}
        lcd_text_widgets(${textWidgetIndex}).visible = ${visible}
        lcd_display_text_widget(lcd_text_widgets(${textWidgetIndex}))
        ${doneBlock}`,
                    });
                    break;
                case 'micropython':
                    blocks.push({
                        location: link.getSourceId(),
                        blockType: BlockType.CODE,
                        id: link.getID(),
                        template: `
${LCDFeature.getSettingValue('NAME')}_widgets[${widgetIndex}].text = ${project.getNodeParameter(link, 'text')}
display_display_widgets()
${doneBlock}`,
                    });
                    break;
                case 'zephyr':
                    blocks.push({
                        location: link.getSourceId(),
                        blockType: BlockType.CODE,
                        id: link.getID(),
                        template: `
    string text_${widget.getID()} = ${project.getNodeParameter(link, 'text')};
    lv_label_set_text(lcd_${widget.getID()}, text_${widget.getID()}.c_str());
    ${doneBlock}`,
                    });
                    break;
                case 'simulator':
                    blocks.push({
                        location: link.getSourceId(),
                        blockType: BlockType.CODE,
                        id: link.getID(),
                        template: `
    simulator.updateContext("${widget.getID()}", {
        ...simulator.context["${widget.getID()}"],
        TEXT: ${project.getNodeParameter(link, 'text')},
        VISIBLE: "${visible}",
    });
    ${doneBlock}`,
                    });
                    break;
                default:
                    break;
            }
        }

        return blocks;
    }

    getDetails(): string {
        return 'Sets the text to be displayed on the LCD screen with the specified position, font, color, and alignment settings of the selected widget.';
    }
}
